(function () {
  'use strict';

  const ROOT_SELECTOR = 'product-information';
  const GATED_FORM_SELECTOR = 'product-form[data-requires-size-selection="true"]';
  const REQUIRED_OPTION_SELECTOR = '[data-required-size-option="true"]';
  const STICKY_ROOT_SELECTOR = '#bundle-sticky_add_to_cart';
  const STICKY_SUBMIT_SELECTOR = '#bundle-sticky_submit';
  const STICKY_DROPDOWN_SELECTOR = '.Vtl-StickyAddToCart__ProductVariantPickerDropdown';
  const STICKY_OPTION_SELECTOR =
    '.Vtl-StickyAddToCart__ProductVariantPickerDropdown-Portal li[role="option"], ' +
    '#bundle-sticky_add_to_cart .Vtl-StickyAddToCart__ProductVariantPicker li[role="option"]';
  const KACHING_GATE_THEME_ID = '161798783204';
  const KACHING_UPSELL_CARD_SELECTOR = '.kaching-product-upsell-item';
  const KACHING_UPSELL_OPTION_SELECTOR =
    '.kaching-product-upsell-item__variant-selector-container select.kaching-cart-selector[data-option-name]';
  const KACHING_UPSELL_BUTTON_SELECTOR =
    '.kaching-product-upsell-item__add-to-cart';
  const KACHING_SIZE_PLACEHOLDER = 'Select a size';
  const KACHING_SIZE_BUTTON_LABEL = 'Select your Size';

  const states = new WeakMap();
  const rootObservers = new WeakMap();
  const registeredRoots = new Set();
  const kachingUpsellStates = new WeakMap();
  const kachingProductRequests = new Map();
  let stickyObserver;
  let stickyApplyScheduled = false;
  let eventBusConnected = false;

  function isElement(node) {
    return node && node.nodeType === Node.ELEMENT_NODE;
  }

  function validVariantId(value) {
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? String(id) : '';
  }

  function setAttributeIfChanged(element, name, value) {
    if (!element) return;
    if (value === null) {
      if (element.hasAttribute(name)) element.removeAttribute(name);
      return;
    }
    if (element.getAttribute(name) !== String(value)) {
      element.setAttribute(name, String(value));
    }
  }

  function setTextIfChanged(element, value) {
    if (element && element.textContent.trim() !== value) {
      element.textContent = value;
    }
  }

  function applyStickyButtonPrompt(submit, label, showPrompt) {
    if (!submit) return;

    const styleProperties = [
      ['--suvaa-size-prompt-font-family', 'fontFamily'],
      ['--suvaa-size-prompt-font-size', 'fontSize'],
      ['--suvaa-size-prompt-font-weight', 'fontWeight'],
      ['--suvaa-size-prompt-letter-spacing', 'letterSpacing'],
      ['--suvaa-size-prompt-line-height', 'lineHeight'],
      ['--suvaa-size-prompt-text-transform', 'textTransform'],
    ];

    if (showPrompt) {
      if (submit.dataset.sizeSelectionPrompt !== 'true') {
        const computedStyle = window.getComputedStyle(submit);
        styleProperties.forEach(function (property) {
          submit.style.setProperty(property[0], computedStyle[property[1]]);
        });
      }
      setAttributeIfChanged(submit, 'data-size-selection-prompt', 'true');
      setAttributeIfChanged(submit, 'data-size-selection-button-label', label);
      setAttributeIfChanged(submit, 'aria-label', label);
      return;
    }

    setAttributeIfChanged(submit, 'data-size-selection-prompt', null);
    setAttributeIfChanged(submit, 'data-size-selection-button-label', null);
    setAttributeIfChanged(submit, 'aria-label', null);
    styleProperties.forEach(function (property) {
      submit.style.removeProperty(property[0]);
    });
  }

  function kachingGateEnabled() {
    return Boolean(
      window.Shopify &&
        window.Shopify.theme &&
        String(window.Shopify.theme.id) === KACHING_GATE_THEME_ID
    );
  }

  function normalizedOptionName(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function isSizeOptionName(value) {
    return /(^|\s)sizes?(\s|$)/.test(normalizedOptionName(value));
  }

  function kachingOptionSelects(card) {
    return card
      ? Array.from(card.querySelectorAll(KACHING_UPSELL_OPTION_SELECTOR))
      : [];
  }

  function kachingSizeSelect(card) {
    return kachingOptionSelects(card).find(function (select) {
      return isSizeOptionName(select.dataset.optionName);
    });
  }

  function ensureKachingSizePlaceholder(select) {
    if (!select) return null;
    let placeholder = select.querySelector(
      'option[data-suvaa-size-placeholder="true"]'
    );
    if (!placeholder) {
      placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.dataset.suvaaSizePlaceholder = 'true';
      placeholder.textContent = KACHING_SIZE_PLACEHOLDER;
      select.insertBefore(placeholder, select.firstChild);
    }
    return placeholder;
  }

  function resetKachingSizeSelect(select) {
    const placeholder = ensureKachingSizePlaceholder(select);
    if (!placeholder) return;
    if (select.value !== '') select.value = '';
    if (!placeholder.selected) placeholder.selected = true;
  }

  function kachingProductURL(handle) {
    const root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    const normalizedRoot = root.endsWith('/') ? root : root + '/';
    return normalizedRoot + 'products/' + encodeURIComponent(handle) + '.js';
  }

  function requestKachingProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!kachingProductRequests.has(handle)) {
      const request = fetch(kachingProductURL(handle), {
        headers: { Accept: 'application/json' },
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Product request failed');
          return response.json();
        })
        .then(function (product) {
          return product && Array.isArray(product.variants) ? product : null;
        })
        .catch(function () {
          return null;
        });
      kachingProductRequests.set(handle, request);
    }
    return kachingProductRequests.get(handle);
  }

  function kachingUpsellState(card) {
    const sizeSelect = kachingSizeSelect(card);
    const productKey = [
      card && card.dataset.productId,
      card && card.dataset.productHandle,
    ].join(':');
    let state = kachingUpsellStates.get(card);
    if (
      !state ||
      state.productKey !== productKey ||
      state.sizeSelect !== sizeSelect
    ) {
      state = {
        productKey: productKey,
        sizeSelect: sizeSelect,
        selected: false,
        explicitSizeValue: '',
        product: null,
        productLoaded: false,
        loadingProduct: false,
        scheduled: false,
      };
      kachingUpsellStates.set(card, state);
    }
    return state;
  }

  function loadKachingUpsellProduct(card, state) {
    const handle = card.dataset.productHandle || '';
    if (!handle || state.loadingProduct || state.productLoaded) return;
    state.loadingProduct = true;
    requestKachingProduct(handle).then(function (product) {
      if (kachingUpsellStates.get(card) !== state) return;
      state.loadingProduct = false;
      state.productLoaded = true;
      state.product = product;
      scheduleKachingUpsell(card);
    });
  }

  function productOptionName(option) {
    return typeof option === 'string' ? option : option && option.name;
  }

  function singleProductOptionValue(option) {
    return option &&
      typeof option !== 'string' &&
      Array.isArray(option.values) &&
      option.values.length === 1
      ? String(option.values[0])
      : '';
  }

  function selectedKachingVariant(card, product) {
    if (!product || !Array.isArray(product.options)) return null;
    const selects = kachingOptionSelects(card);
    const selectedValues = product.options.map(function (option) {
      const name = normalizedOptionName(productOptionName(option));
      const select = selects.find(function (candidate) {
        return normalizedOptionName(candidate.dataset.optionName) === name;
      });
      return select ? select.value : singleProductOptionValue(option);
    });
    if (selectedValues.some(function (value) { return !value; })) return null;

    return product.variants.find(function (variant) {
      const options = Array.isArray(variant.options)
        ? variant.options
        : [variant.option1, variant.option2, variant.option3];
      return selectedValues.every(function (value, index) {
        return String(options[index] || '') === String(value);
      });
    }) || null;
  }

  function applyKachingButtonPrompt(button, showPrompt) {
    if (!button) return;
    const styleProperties = [
      ['--suvaa-kaching-size-prompt-font-family', 'fontFamily'],
      ['--suvaa-kaching-size-prompt-font-size', 'fontSize'],
      ['--suvaa-kaching-size-prompt-font-weight', 'fontWeight'],
      ['--suvaa-kaching-size-prompt-letter-spacing', 'letterSpacing'],
      ['--suvaa-kaching-size-prompt-line-height', 'lineHeight'],
      ['--suvaa-kaching-size-prompt-text-transform', 'textTransform'],
    ];

    if (showPrompt) {
      if (button.dataset.suvaaKachingSizeGated !== 'true') {
        const computedStyle = window.getComputedStyle(button);
        styleProperties.forEach(function (property) {
          button.style.setProperty(property[0], computedStyle[property[1]]);
        });
      }
      setAttributeIfChanged(button, 'data-suvaa-kaching-size-gated', 'true');
      setAttributeIfChanged(button, 'data-size-selection-prompt', 'true');
      setAttributeIfChanged(
        button,
        'data-size-selection-button-label',
        KACHING_SIZE_BUTTON_LABEL
      );
      setAttributeIfChanged(button, 'aria-disabled', 'true');
      setAttributeIfChanged(button, 'aria-label', KACHING_SIZE_BUTTON_LABEL);
      setAttributeIfChanged(button, 'inert', '');
      return;
    }

    setAttributeIfChanged(button, 'data-suvaa-kaching-size-gated', null);
    setAttributeIfChanged(button, 'data-size-selection-prompt', null);
    setAttributeIfChanged(button, 'data-size-selection-button-label', null);
    setAttributeIfChanged(button, 'aria-disabled', null);
    setAttributeIfChanged(button, 'aria-label', null);
    setAttributeIfChanged(button, 'inert', null);
    styleProperties.forEach(function (property) {
      button.style.removeProperty(property[0]);
    });
  }

  function applyKachingUpsell(card) {
    if (!kachingGateEnabled() || !card || !card.isConnected) return;
    const state = kachingUpsellState(card);
    const select = state.sizeSelect;
    const button = card.querySelector(KACHING_UPSELL_BUTTON_SELECTOR);
    if (!select || !button) return;

    ensureKachingSizePlaceholder(select);
    setAttributeIfChanged(select, 'data-suvaa-size-selection-required', 'true');
    setAttributeIfChanged(select, 'aria-required', 'true');

    if (
      !state.selected ||
      !select.value ||
      select.value !== state.explicitSizeValue
    ) {
      state.selected = false;
      state.explicitSizeValue = '';
      resetKachingSizeSelect(select);
    }
    if (state.selected) loadKachingUpsellProduct(card, state);

    const selectedVariant = state.selected
      ? selectedKachingVariant(card, state.product)
      : null;
    const expectedVariantId = validVariantId(
      selectedVariant && selectedVariant.id
    );
    const synced =
      Boolean(expectedVariantId) &&
      validVariantId(card.dataset.variantId) === expectedVariantId;
    const gated = !state.selected || !expectedVariantId || !synced;

    setAttributeIfChanged(
      card,
      'data-suvaa-size-selection-state',
      gated ? (state.selected ? 'syncing' : 'missing') : 'selected'
    );
    applyKachingButtonPrompt(button, gated);
  }

  function scheduleKachingUpsell(card) {
    if (!card || !kachingGateEnabled()) return;
    const state = kachingUpsellState(card);
    if (state.scheduled) return;
    state.scheduled = true;
    Promise.resolve().then(function () {
      state.scheduled = false;
      if (card.isConnected) applyKachingUpsell(card);
    });
  }

  function scanForKachingUpsells(scope) {
    if (!kachingGateEnabled()) return;
    if (!isElement(scope) && scope !== document) return;
    if (isElement(scope) && scope.matches(KACHING_UPSELL_CARD_SELECTOR)) {
      scheduleKachingUpsell(scope);
    }
    scope.querySelectorAll(KACHING_UPSELL_CARD_SELECTOR).forEach(
      scheduleKachingUpsell
    );
  }

  function blockKachingUpsell(event, button) {
    const card = button && button.closest(KACHING_UPSELL_CARD_SELECTOR);
    if (!card) return false;
    applyKachingUpsell(card);
    if (button.dataset.suvaaKachingSizeGated !== 'true') return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    return true;
  }

  function gateForm(root) {
    return root ? root.querySelector(GATED_FORM_SELECTOR) : null;
  }

  function getRoot(node) {
    if (!isElement(node)) return null;
    const root = node.closest(ROOT_SELECTOR);
    return root && gateForm(root) ? root : null;
  }

  function stateFor(root) {
    let state = states.get(root);
    if (!state) {
      state = {
        selected: false,
        variantId: '',
        variantTitle: '',
        available: false,
        scheduled: false,
        notifiedInput: null,
        checkoutVariantId: '',
      };
      states.set(root, state);
    }
    return state;
  }

  function variantFromThemeControl(control) {
    if (!control) return null;

    const swatch = control.closest('.swatch-element[data-swatch-variant-id]');
    if (swatch) {
      return {
        id: validVariantId(swatch.dataset.swatchVariantId),
        title: control.value || swatch.dataset.value || '',
        available:
          swatch.dataset.swatchOptionAvailable !== 'false' &&
          !swatch.classList.contains('soldout'),
      };
    }

    const dropdownOption = control.closest('li[data-dropdown-variant-id]');
    if (dropdownOption) {
      return {
        id: validVariantId(dropdownOption.dataset.dropdownVariantId),
        title: dropdownOption.dataset.optionValue || dropdownOption.textContent.trim(),
        available: !dropdownOption.classList.contains('sold-out'),
      };
    }

    return null;
  }

  function findThemeVariant(root, variantId) {
    const id = validVariantId(variantId);
    if (!id) return null;

    for (const dataElement of root.querySelectorAll('script[type="application/json"][data-resource]')) {
      try {
        const variant = JSON.parse(dataElement.textContent);
        if (String(variant.id) === id) return variant;
      } catch (error) {
        // Ignore malformed third-party data and keep the gate closed.
      }
    }
    return null;
  }

  function squareVariants() {
    return window.vtlsLiquidData &&
      window.vtlsLiquidData.product &&
      Array.isArray(window.vtlsLiquidData.product.variants)
      ? window.vtlsLiquidData.product.variants
      : [];
  }

  function squareVariantById(variantId) {
    const id = validVariantId(variantId);
    return squareVariants().find(function (variant) {
      return String(variant.id) === id;
    });
  }

  function squareVariantFromOption(option) {
    if (!option) return null;
    const labelElement = option.querySelector(
      '.Vtl-Dropdown__Portal-List-Option-Label, .Vtl-Dropdown__List-Option-Label, [class*="List-Option-Label"]'
    );
    const label = (labelElement || option).textContent.trim();
    const variants = squareVariants();
    const exactMatch = variants.find(function (variant) {
      return variant.title.trim() === label;
    });
    if (exactMatch) return exactMatch;

    const siblings = Array.from(option.parentElement.querySelectorAll('li[role="option"]'));
    const index = siblings.indexOf(option);
    return index >= 0 ? variants[index] : null;
  }

  function clearRequiredOptionUi(root, placeholder) {
    root.querySelectorAll(
      REQUIRED_OPTION_SELECTOR + ' input[data-product-variant]'
    ).forEach(function (input) {
      input.checked = false;
      input.removeAttribute('checked');
      const swatch = input.closest('.swatch-element');
      if (swatch) swatch.classList.remove('active');
    });

    root.querySelectorAll(
      REQUIRED_OPTION_SELECTOR + ' li[data-product-variant]'
    ).forEach(function (option) {
      option.removeAttribute('selected');
      option.classList.remove('selected');
    });

    root.querySelectorAll(
      REQUIRED_OPTION_SELECTOR + ' [data-selected-option]'
    ).forEach(function (label) {
      setTextIfChanged(label, placeholder);
    });
  }

  function selectRequiredOptionUi(root, state, placeholder) {
    let selectedLabel = '';
    root.querySelectorAll(
      REQUIRED_OPTION_SELECTOR + ' .swatch-element[data-swatch-variant-id]'
    ).forEach(function (swatch) {
      const isSelected =
        Boolean(state.variantId) &&
        String(swatch.dataset.swatchVariantId) === state.variantId;
      const input = swatch.querySelector('input[data-product-variant]');
      if (input) {
        if (input.checked !== isSelected) input.checked = isSelected;
        setAttributeIfChanged(input, 'checked', isSelected ? '' : null);
      }
      swatch.classList.toggle('active', isSelected);
      if (isSelected) selectedLabel = input ? input.value : swatch.dataset.value || '';
    });

    root.querySelectorAll(
      REQUIRED_OPTION_SELECTOR + ' li[data-dropdown-variant-id]'
    ).forEach(function (option) {
      const isSelected =
        Boolean(state.variantId) &&
        String(option.dataset.dropdownVariantId) === state.variantId;
      setAttributeIfChanged(option, 'selected', isSelected ? '' : null);
      option.classList.toggle('selected', isSelected);
      const optionLabel = option.querySelector('[data-option-label]');
      if (isSelected && optionLabel) selectedLabel = optionLabel.textContent.trim();
    });

    root.querySelectorAll(
      REQUIRED_OPTION_SELECTOR + ' [data-selected-option]'
    ).forEach(function (label) {
      setTextIfChanged(label, selectedLabel || state.variantTitle || placeholder);
    });
  }

  function notifyVariantInput(root, state, input) {
    if (!state.selected || !input || state.notifiedInput === input) return;
    state.notifiedInput = input;
    Promise.resolve().then(function () {
      if (
        input.isConnected &&
        state.selected &&
        input.value === state.variantId
      ) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function initializePaymentButton(state) {
    if (
      !state.selected ||
      !state.available ||
      !state.variantId ||
      state.checkoutVariantId === state.variantId
    ) {
      return;
    }
    state.checkoutVariantId = state.variantId;
    Promise.resolve().then(function () {
      if (window.Shopify && window.Shopify.PaymentButton) {
        window.Shopify.PaymentButton.init();
      }
    });
  }

  function applyRoot(root) {
    const formComponent = gateForm(root);
    if (!formComponent) return;

    const state = stateFor(root);
    const placeholder =
      formComponent.dataset.sizeSelectionPlaceholder || 'Select a size';
    const buttonLabel =
      formComponent.dataset.sizeSelectionButtonLabel || 'Select your Size';
    const ready =
      state.selected &&
      Boolean(state.variantId) &&
      state.available !== false;

    setAttributeIfChanged(
      root,
      'data-size-selection-state',
      state.selected ? 'selected' : 'missing'
    );
    setAttributeIfChanged(
      formComponent,
      'data-size-selection-missing',
      state.selected ? 'false' : 'true'
    );

    if (state.selected) {
      selectRequiredOptionUi(root, state, placeholder);
    } else {
      clearRequiredOptionUi(root, placeholder);
      state.notifiedInput = null;
      state.checkoutVariantId = '';
    }

    const variantInputs = formComponent.querySelectorAll(
      'input[name="id"][data-variant-selection-required]'
    );
    variantInputs.forEach(function (input) {
      const nextValue = state.selected ? state.variantId : '';
      if (input.value !== nextValue) input.value = nextValue;
      setAttributeIfChanged(input, 'value', nextValue);
      if (input.disabled !== !ready) input.disabled = !ready;
      setAttributeIfChanged(input, 'disabled', ready ? null : '');
      notifyVariantInput(root, state, input);
    });

    formComponent.querySelectorAll(
      '[name="add"][data-variant-selection-required]'
    ).forEach(function (button) {
      const showPrompt = !state.selected || !state.variantId;
      setAttributeIfChanged(
        button,
        'data-size-selection-prompt',
        showPrompt ? 'true' : null
      );
      setAttributeIfChanged(
        button,
        'data-size-selection-button-label',
        showPrompt ? buttonLabel : null
      );
      setAttributeIfChanged(button, 'aria-label', showPrompt ? buttonLabel : null);

      if (!ready) {
        setAttributeIfChanged(button, 'data-size-gate-disabled', 'true');
        if (!button.disabled) button.disabled = true;
        setAttributeIfChanged(button, 'disabled', '');
      } else if (button.dataset.sizeGateDisabled === 'true') {
        setAttributeIfChanged(button, 'data-size-gate-disabled', null);
        if (button.disabled) button.disabled = false;
        setAttributeIfChanged(button, 'disabled', null);
      }
      setAttributeIfChanged(
        button,
        'aria-disabled',
        button.disabled ? 'true' : 'false'
      );
    });

    formComponent.querySelectorAll(
      '[data-variant-selection-checkout]'
    ).forEach(function (checkout) {
      if (ready) {
        checkout.hidden = false;
        setAttributeIfChanged(checkout, 'hidden', null);
        setAttributeIfChanged(checkout, 'inert', null);
        setAttributeIfChanged(checkout, 'aria-hidden', 'false');
      } else {
        checkout.hidden = true;
        setAttributeIfChanged(checkout, 'hidden', '');
        setAttributeIfChanged(checkout, 'inert', '');
        setAttributeIfChanged(checkout, 'aria-hidden', 'true');
      }
    });

    if (ready) initializePaymentButton(state);
    scheduleStickyApply();
  }

  function scheduleRoot(root) {
    const state = stateFor(root);
    if (state.scheduled) return;
    state.scheduled = true;
    Promise.resolve().then(function () {
      state.scheduled = false;
      if (root.isConnected) applyRoot(root);
    });
  }

  function markExplicitSelection(root, variant) {
    const state = stateFor(root);
    const id = validVariantId(variant && variant.id);
    if (!id) {
      scheduleRoot(root);
      return false;
    }

    state.selected = true;
    state.variantId = id;
    state.variantTitle = (variant && variant.title) || '';
    state.available = !variant || variant.available !== false;
    state.notifiedInput = null;
    applyRoot(root);
    return true;
  }

  function resetExplicitSelection(root) {
    const state = stateFor(root);
    state.selected = false;
    state.variantId = '';
    state.variantTitle = '';
    state.available = false;
    state.notifiedInput = null;
    state.checkoutVariantId = '';
    applyRoot(root);
  }

  function updateSelectedVariant(root, variant) {
    const state = stateFor(root);
    if (!state.selected) {
      scheduleRoot(root);
      return;
    }
    if (!variant) {
      resetExplicitSelection(root);
      return;
    }
    const id = validVariantId(variant.id);
    if (!id) return;
    state.variantId = id;
    state.variantTitle = variant.title || state.variantTitle;
    state.available = variant.available !== false;
    state.notifiedInput = null;
    applyRoot(root);
  }

  function rootForSection(sectionId) {
    for (const root of registeredRoots) {
      if (root.isConnected && root.dataset.sectionId === String(sectionId)) {
        return root;
      }
    }
    return null;
  }

  function variantFetchContext(root, variant) {
    const id = validVariantId(variant && variant.id);
    if (!id) return null;

    let fetchURL = '';
    let productURL = root.dataset.url || window.location.pathname;
    const controls = root.querySelectorAll(
      '[data-swatch-variant-id], [data-dropdown-variant-id]'
    );
    for (const control of controls) {
      const controlId =
        control.dataset.swatchVariantId || control.dataset.dropdownVariantId;
      if (String(controlId) !== id) continue;
      const dataElement = control.matches('[data-product-fetch-url]')
        ? control
        : control.querySelector('[data-product-fetch-url]');
      if (dataElement) {
        fetchURL = dataElement.dataset.productFetchUrl || '';
        productURL = dataElement.dataset.productUrl || productURL;
      }
      if (fetchURL) break;
    }

    if (!fetchURL) {
      const separator = productURL.indexOf('?') === -1 ? '?' : '&';
      fetchURL = productURL + separator + 'variant=' + id;
    }

    return {
      sectionId: root.dataset.sectionId,
      variant: findThemeVariant(root, id) || variant,
      fetchURL: fetchURL,
      productURL: productURL,
      explicitSizeSelection: true,
    };
  }

  function selectFromSquare(root, variant) {
    if (!root || !variant || !markExplicitSelection(root, variant)) return;
    const context = variantFetchContext(root, variant);
    if (!context) return;

    Promise.resolve().then(function () {
      if (window.eventBus && typeof window.eventBus.emit === 'function') {
        window.eventBus.emit('variant:change', context);
      }
    });
  }

  function mainRoot() {
    let fallback = null;
    for (const root of registeredRoots) {
      if (
        root.isConnected &&
        root.dataset.quickView !== 'true' &&
        gateForm(root)
      ) {
        if (
          root.dataset.enableUrlUpdate === 'true' &&
          root.dataset.isFeaturedProduct !== 'true'
        ) {
          return root;
        }
        if (!fallback) fallback = root;
      }
    }
    return fallback;
  }

  function stickyVariantMatches(state, dropdown) {
    if (!state || !state.selected || !state.variantId) return false;
    const variants = squareVariants();
    if (variants.length === 1) {
      return String(variants[0].id) === state.variantId;
    }
    return (
      validVariantId(dropdown && dropdown.dataset.selectedValue) ===
      state.variantId
    );
  }

  function applySticky() {
    const root = mainRoot();
    const state = root ? stateFor(root) : null;
    const dropdown = document.querySelector(STICKY_DROPDOWN_SELECTOR);
    const stickyVariantSynced =
      Boolean(root) && stickyVariantMatches(state, dropdown);
    const missing =
      Boolean(root) &&
      (!state.selected ||
        !state.variantId ||
        state.available === false ||
        !stickyVariantSynced);
    const placeholder = root
      ? gateForm(root).dataset.sizeSelectionPlaceholder || 'Select a size'
      : 'Select a size';
    const buttonLabel = root
      ? gateForm(root).dataset.sizeSelectionButtonLabel || 'Select your Size'
      : 'Select your Size';
    const showButtonPrompt =
      Boolean(root) &&
      (!state.selected || !state.variantId || !stickyVariantSynced);

    document.documentElement.classList.toggle(
      'variant-selection-required',
      Boolean(root) && !stickyVariantSynced
    );

    const submit = document.querySelector(STICKY_SUBMIT_SELECTOR);
    if (submit) {
      if (missing) {
        setAttributeIfChanged(submit, 'data-variant-selection-gated', 'true');
        setAttributeIfChanged(submit, 'aria-disabled', 'true');
      } else {
        setAttributeIfChanged(submit, 'data-variant-selection-gated', null);
        setAttributeIfChanged(submit, 'aria-disabled', 'false');
      }
      applyStickyButtonPrompt(submit, buttonLabel, showButtonPrompt);
    }

    const selectedHeader =
      dropdown && dropdown.querySelector('.Vtl-Dropdown__Header-Selected');
    const header = dropdown && dropdown.querySelector('.Vtl-Dropdown__Header');

    if (dropdown && root) {
      setAttributeIfChanged(
        selectedHeader,
        'data-variant-selection-placeholder',
        placeholder
      );
      if (!state.selected || !stickyVariantSynced) {
        setTextIfChanged(selectedHeader, placeholder);
        setAttributeIfChanged(header, 'aria-label', placeholder);
      } else if (stickyVariantSynced) {
        const squareVariant = squareVariantById(state.variantId);
        const title =
          (squareVariant && squareVariant.title) ||
          state.variantTitle ||
          placeholder;
        setTextIfChanged(selectedHeader, title);
        setAttributeIfChanged(header, 'aria-label', title);
      }
    }

    document.querySelectorAll(STICKY_OPTION_SELECTOR).forEach(function (option) {
      if (!root || !state.selected || !stickyVariantSynced) {
        setAttributeIfChanged(option, 'aria-selected', 'false');
        return;
      }
      const variant = squareVariantFromOption(option);
      setAttributeIfChanged(
        option,
        'aria-selected',
        variant && String(variant.id) === state.variantId ? 'true' : 'false'
      );
    });
  }

  function scheduleStickyApply() {
    if (stickyApplyScheduled) return;
    stickyApplyScheduled = true;
    Promise.resolve().then(function () {
      stickyApplyScheduled = false;
      applySticky();
      observeSticky();
    });
  }

  function observeSticky() {
    const stickyRoot = document.querySelector(STICKY_ROOT_SELECTOR);
    if (!stickyRoot) {
      if (stickyObserver) stickyObserver.observer.disconnect();
      stickyObserver = undefined;
      return;
    }
    if (stickyObserver && stickyObserver.root === stickyRoot) {
      return;
    }
    if (stickyObserver) stickyObserver.observer.disconnect();

    const observer = new MutationObserver(scheduleStickyApply);
    observer.observe(stickyRoot, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['aria-disabled', 'aria-selected', 'data-selected-value'],
    });
    stickyObserver = { root: stickyRoot, observer: observer };
  }

  function registerRoot(root) {
    if (!root || !gateForm(root) || rootObservers.has(root)) return;
    registeredRoots.add(root);
    stateFor(root);

    const observer = new MutationObserver(function () {
      scheduleRoot(root);
    });
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'value', 'checked', 'selected'],
    });
    rootObservers.set(root, observer);
    applyRoot(root);
  }

  function unregisterRoot(root) {
    if (!root || root.isConnected) return;
    const observer = rootObservers.get(root);
    if (observer) observer.disconnect();
    rootObservers.delete(root);
    states.delete(root);
    registeredRoots.delete(root);
  }

  function unregisterRoots(scope) {
    if (!isElement(scope)) return;
    if (scope.matches(ROOT_SELECTOR)) unregisterRoot(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(unregisterRoot);
  }

  function scanForRoots(scope) {
    if (!isElement(scope) && scope !== document) return;
    if (isElement(scope) && scope.matches(ROOT_SELECTOR)) {
      registerRoot(scope);
    }
    scope.querySelectorAll(ROOT_SELECTOR).forEach(registerRoot);
  }

  function blockPurchase(event, root, requireStickySync) {
    const state = stateFor(root);
    const input = gateForm(root).querySelector(
      'input[name="id"][data-variant-selection-required]'
    );
    const ready =
      state.selected &&
      state.available !== false &&
      input &&
      !input.disabled &&
      validVariantId(input.value) === state.variantId &&
      (!requireStickySync ||
        stickyVariantMatches(
          state,
          document.querySelector(STICKY_DROPDOWN_SELECTOR)
        ));
    if (ready) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    applyRoot(root);
    return true;
  }

  function onDocumentClick(event) {
    if (!isElement(event.target)) return;

    const kachingButton = event.target.closest(KACHING_UPSELL_BUTTON_SELECTOR);
    if (kachingButton && blockKachingUpsell(event, kachingButton)) return;

    const stickySubmit = event.target.closest(STICKY_SUBMIT_SELECTOR);
    if (stickySubmit) {
      const root = mainRoot();
      if (root && blockPurchase(event, root, true)) return;
    }

    const stickyOption = event.target.closest(STICKY_OPTION_SELECTOR);
    if (stickyOption && event.isTrusted) {
      const root = mainRoot();
      const variant = squareVariantFromOption(stickyOption);
      if (root && variant) selectFromSquare(root, variant);
      return;
    }

    const option = event.target.closest('li[data-product-variant]');
    if (option) {
      const root = getRoot(option);
      if (!root) return;
      const variant = variantFromThemeControl(option);
      if (option.closest(REQUIRED_OPTION_SELECTOR) && event.isTrusted) {
        markExplicitSelection(root, variant);
      } else if (event.isTrusted && stateFor(root).selected) {
        updateSelectedVariant(root, variant);
      } else {
        scheduleRoot(root);
      }
      return;
    }

    const root = getRoot(event.target);
    if (!root) return;
    if (
      event.target.closest(
        '[name="add"], .shopify-payment-button, shopify-accelerated-checkout'
      )
    ) {
      blockPurchase(event, root);
    }
  }

  function onDocumentChange(event) {
    if (!isElement(event.target)) return;

    const kachingSelect = event.target.closest(KACHING_UPSELL_OPTION_SELECTOR);
    if (kachingSelect) {
      const card = kachingSelect.closest(KACHING_UPSELL_CARD_SELECTOR);
      if (!card) return;
      const state = kachingUpsellState(card);
      if (
        event.isTrusted &&
        isSizeOptionName(kachingSelect.dataset.optionName)
      ) {
        state.selected = Boolean(kachingSelect.value);
        state.explicitSizeValue = state.selected ? kachingSelect.value : '';
      }
      scheduleKachingUpsell(card);
      return;
    }

    const control = event.target.closest('input[data-product-variant]');
    if (!control) return;
    const root = getRoot(control);
    if (!root) return;

    const variant = variantFromThemeControl(control);
    if (control.closest(REQUIRED_OPTION_SELECTOR) && event.isTrusted) {
      markExplicitSelection(root, variant);
    } else if (event.isTrusted && stateFor(root).selected) {
      updateSelectedVariant(root, variant);
    } else {
      scheduleRoot(root);
    }
  }

  function onDocumentKeydown(event) {
    if (!isElement(event.target)) return;

    const themeOption = event.target.closest(
      REQUIRED_OPTION_SELECTOR + ' li[data-product-variant]'
    );
    if (
      themeOption &&
      event.isTrusted &&
      event.key === 'Enter'
    ) {
      const root = getRoot(themeOption);
      if (root) markExplicitSelection(root, variantFromThemeControl(themeOption));
      return;
    }

    const stickyHeader = event.target.closest(
      STICKY_DROPDOWN_SELECTOR + ' .Vtl-Dropdown__Header'
    );
    if (
      !stickyHeader ||
      !event.isTrusted ||
      (event.key !== 'Enter' && event.key !== ' ') ||
      stickyHeader.getAttribute('aria-expanded') !== 'true'
    ) {
      return;
    }

    const activeOptionId = stickyHeader.getAttribute('aria-activedescendant');
    const activeOption = activeOptionId && document.getElementById(activeOptionId);
    const root = mainRoot();
    const variant = squareVariantFromOption(activeOption);
    if (root && variant) selectFromSquare(root, variant);
  }

  function onDocumentSubmit(event) {
    if (!isElement(event.target)) return;
    const root = getRoot(event.target);
    if (root) blockPurchase(event, root);
  }

  function connectEventBus(attempt) {
    if (eventBusConnected) return;
    if (!window.eventBus || typeof window.eventBus.on !== 'function') {
      if (attempt < 40) {
        window.setTimeout(function () {
          connectEventBus(attempt + 1);
        }, 50);
      }
      return;
    }

    eventBusConnected = true;
    window.eventBus.on('variant:change', function (context) {
      const root = context && rootForSection(context.sectionId);
      if (!root) return;
      if (stateFor(root).selected) {
        updateSelectedVariant(root, context && context.variant);
      } else {
        scheduleRoot(root);
      }
    });
    window.eventBus.on('variant:updated', function (context) {
      const root = context && rootForSection(context.sectionId);
      if (!root) return;
      if (stateFor(root).selected) {
        updateSelectedVariant(root, context && context.variant);
      } else {
        scheduleRoot(root);
      }
    });
  }

  function init() {
    scanForRoots(document);
    scanForKachingUpsells(document);
    connectEventBus(0);
    observeSticky();
    scheduleStickyApply();

    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('change', onDocumentChange, true);
    document.addEventListener('keydown', onDocumentKeydown, true);
    document.addEventListener('submit', onDocumentSubmit, true);

    const documentObserver = new MutationObserver(function (mutations) {
      const removedNodes = [];
      mutations.forEach(function (mutation) {
        const mutationTarget = isElement(mutation.target)
          ? mutation.target.closest(KACHING_UPSELL_CARD_SELECTOR)
          : null;
        if (mutationTarget) scheduleKachingUpsell(mutationTarget);
        mutation.addedNodes.forEach(function (node) {
          if (isElement(node)) {
            scanForRoots(node);
            scanForKachingUpsells(node);
          }
        });
        mutation.removedNodes.forEach(function (node) {
          if (isElement(node)) removedNodes.push(node);
        });
      });
      removedNodes.forEach(unregisterRoots);
      observeSticky();
      scheduleStickyApply();
    });
    documentObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-variant-id', 'data-product-handle'],
    });

    window.SuvaaVariantSelectionGate = {
      getState: function () {
        const root = mainRoot();
        if (!root) return null;
        const state = stateFor(root);
        return {
          selected: state.selected,
          variantId: state.variantId,
          available: state.available,
          themeId:
            window.Shopify && window.Shopify.theme
              ? String(window.Shopify.theme.id)
              : '',
        };
      },
      refresh: function () {
        registeredRoots.forEach(scheduleRoot);
        scanForKachingUpsells(document);
        scheduleStickyApply();
      },
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
